import StringCondition from '../filters/conditions/String';

import NumberCondition from '../filters/conditions/Number';
import BooleanCondition from '../filters/conditions/Boolean';
import ListConditions from '../filters/conditions/ListConditions';
import TimestampCondition from '../filters/conditions/Timestamp';

import StringDisplay from '../../abstract-search/projections/column-type-display/StringDisplay';
import NumberComponent from '../../../elements/Number';

import {sortBy, groupBy} from 'underscore';
import BooleanDisplay from "../projections/column-type-display/BooleanDisplay";
import TimeSince from "$flx/elements/TimeSince";
import NameValueFilterCondition from "$flx/components/abstract-search/filters/conditions/NameValueFilterCondition";

const util = {};

util.nullComparators = {
    is_null : {
        handle : 'is_null',
        label : 'Empty',
        comparatorComponent : null,
    },
    is_not_null : {
        handle : 'is_not_null',
        label : 'Not Empty',
        comparatorComponent : null,
    },
};

util.stringComparators = (component) => {
    return {
        match_exact : {
            handle : 'match_exact',
            label : 'Exact Match',
            comparatorComponent : component,
        },
        match_fuzzy : {
            handle : 'match_fuzzy',
            label : 'Fuzzy Match',
            comparatorComponent : component,
        },
        starts_with : {
            handle : 'starts_with',
            label : 'Starts With',
            comparatorComponent : component,
        },
        ends_with : {
            handle : 'ends_with',
            label : 'Ends With',
            comparatorComponent : component,
        },
        is_null : {
            handle : 'is_null',
            label : 'Empty',
            comparatorComponent : component,
        },
        is_not_null : {
            handle : 'is_not_null',
            label : 'Not Empty',
            comparatorComponent : component,
        },
        is_not : {
            handle : 'is_not',
            label : 'Is Not',
            comparatorComponent : component,
        },
        does_not_contain : {
            handle : 'does_not_contain',
            label : 'Does Not Contain',
            comparatorComponent : component,
        },
        ...util.nullComparators
    };
};

util.baseComparators = (component) => {
    return {
        equal : {
            handle : 'equal',
            label : 'Equals',
            comparatorComponent : component,
        },
        not_equal : {
            handle : 'not_equal',
            label : 'Does Not Equal',
            comparatorComponent : component,
        },
        ...util.nullComparators
    };
};

util.booleanComparators = () => {
    return {

    };
};

util.rangeComparators = (prefix, component) => {
    return {
        greater : {
            handle : `${prefix}greater`,
            label : 'Greater Than',
            comparatorComponent : component,
        },
        greater_equal : {
            handle : `${prefix}greater_equal`,
            label : 'Greater Than Or Equal To',
            comparatorComponent : component,
        },
        less : {
            handle : `${prefix}less`,
            label : 'Less Than',
            comparatorComponent : component,
        },
        less_equal : {
            handle : `${prefix}less_equal`,
            label : 'Less Than Or Equal To',
            comparatorComponent : component,
        },
    };
};

util.DATA_TYPES = {
    string : {
        handle : 'string',
        comparators : util.stringComparators(StringCondition),
    },
    number : {
        handle : 'number',
        comparators : {
            ...util.baseComparators(NumberCondition),
            ...util.rangeComparators('', NumberCondition),
        },
    },
    boolean : {
        handle : 'boolean',
        comparators : {
            equal : {
                handle : 'boolean_equal',
                label : 'Equals',
                comparatorComponent : BooleanCondition,
            },
        },
    },
    booleanNullable : {
        handle : 'boolean_nullable',
        comparators : {
            equal : {
                handle : 'boolean_equal_nullable',
                label : 'Equals',
                comparatorComponent : BooleanCondition,
            },
        },
    },
    notNull : {
        handle : 'not_null',
        comparators : {
            equal : {
                handle : 'not_null_equal',
                label : 'Equals',
                comparatorComponent : BooleanCondition,
            },
        },
    },
    list : {
        handle : 'list',
        comparators : {
            equal : {
                handle : 'list_empty',
                label : 'Equals', //label doesn't matter since only one comparator and wont display
                hideLabel : true,
                comparatorComponent : ListConditions,
            },
        },
    },
    timestamp : {
        handle : 'timestamp',
        comparators : {
            greater : {
                handle : `time_greater`,
                label : 'Older Than',
                comparatorComponent : TimestampCondition,
            },
            less_equal : {
                handle : `time_less_equal`,
                label : 'Within Last',
                comparatorComponent : TimestampCondition,
            },
            ...util.nullComparators
        },
    },
    nameValue : {
        handle : 'name_value',
        customTypeCondition : NameValueFilterCondition,
        comparators : {},
    },
};

function removeGroupPrefix (value, groupName) {
    if(value.startsWith(groupName + ' ')){
        return value.substring(groupName.length + 1);
    }
    return value;
}

util.createField = (name, handle, group, type, extra) => {
    if(!extra){
        extra = {};
    }
    if(!extra.displayComponent){
        if(type.handle === 'number') {
            extra.displayComponent = NumberComponent;
        } else if(type.handle === 'timestamp') {
            extra.displayComponent = TimeSince;
            extra.componentProps = { position : 'left' };
        } else if(type.handle === 'boolean') {
            extra.displayComponent = BooleanDisplay;
        } else {
            extra.displayComponent = StringDisplay;
        }
    }
    if(!extra.contextKey){
        extra.contextKey = handle.split('.')[1];
    }
    return {
        name : name,
        nameNoPrefix : removeGroupPrefix(name, group),
        handle : handle,
        group : group,
        type : type,
        ...extra
    };
};

util.searchableFieldTypes = [
    StringDisplay.name,
];

util.searchableFields =  (fields) => {
    return fields.filter(f => {
        return f.searchable !== false && (f.searchable || util.searchableFieldTypes.indexOf(f.displayComponent.name) !== -1);
    });
};

util.groupFieldsWithNiceOrder = (fields, groupNamesInOrder) => {
    const groups = groupBy(fields, 'group');
    const list = [];
    groupNamesInOrder.forEach(groupName => {
        if(!groups[groupName]){
            return;
        }
        list.push({ groupName : groupName, fields : sortBy(groups[groupName], 'nameNoPrefix') });
    });
    return list;
};

util.buildAvailableFields = (fields, groupNamesInOrder) => {
    fields = sortBy(fields, 'name');
    const searchableFields = util.searchableFields(fields);
    return {
        DATA_TYPES : util.DATA_TYPES,
        groupFieldsWithNiceOrder : util.groupFieldsWithNiceOrder,
        fields : fields,
        groupedFields : util.groupFieldsWithNiceOrder(fields, groupNamesInOrder),
        searchableFieldTypes : util.searchableFieldTypes,
        searchableFields : searchableFields,
        searchableFieldsGroups : util.groupFieldsWithNiceOrder(searchableFields, groupNamesInOrder),
        searchableFieldHandles : searchableFields.map(f => f.handle),
    };
};

export default util;
